import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import "cropperjs/dist/cropper.css"

const LONG_EDGE = 1200

const RATIO_LABELS = {
  "1": "1x1",
  "0.8": "4x5",
  "0.75": "3x4"
}

// Uploads original product images and stores crop geometry as form metadata.
export default class extends Controller {
  static targets = [
    "picker",
    "input",
    "crops",
    "cropUpdates",
    "replacements",
    "modal",
    "cropImage",
    "filename",
    "pendingList",
    "existingItem",
    "ratioButton"
  ]

  static values = {
    aspectRatio: { type: Number, default: 1 },
    outputSize: { type: Number, default: LONG_EDGE },
    pendingCountLabel: { type: String, default: "%{count} imágenes" }
  }

  connect () {
    this.queue = []
    this.cropper = null
    this.currentSourceName = "image.jpg"
    this.currentOriginalFile = null
    this.pendingCropData = null
    this.pendingEntries = []
    this.pendingObjectUrls = []
    this.editingAttachmentId = null
    this.selectedAspectRatio = this.aspectRatioValue
    this.syncRatioButtons()
  }

  disconnect () {
    this.revokePendingObjectUrls()
    this.destroyCropper()
  }

  openPicker (event) {
    event.preventDefault()
    this.pickerTarget.click()
  }

  select (event) {
    const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith("image/"))
    event.target.value = ""
    if (files.length === 0) return

    this.editingAttachmentId = null
    this.pendingCropData = null
    this.queue = files.map((file) => ({ file, originalFile: file }))
    this.processQueue()
  }

  editExisting (event) {
    event.preventDefault()
    const button = event.currentTarget
    const originalUrl = button.dataset.originalUrl || button.dataset.imageUrl
    const filename = button.dataset.filename || "image.jpg"
    const attachmentId = button.dataset.attachmentId
    if (!originalUrl) return

    this.editingAttachmentId = attachmentId
    this.currentOriginalFile = null
    this.queue = []
    this.pendingCropData = this.parseCropJson(button.dataset.cropJson)

    if (this.pendingCropData?.ratio) {
      this.selectedAspectRatio = Number(this.pendingCropData.ratio)
      this.syncRatioButtons()
    }

    this.openCropper(originalUrl, filename)
  }

  markExistingForRemoval (event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-product-images-crop-target='existingItem']")
    if (!item) return

    const checkbox = item.querySelector('input[type="checkbox"][name="product[purge_image_ids][]"]')
    if (checkbox) checkbox.checked = true
    item.classList.add("opacity-40", "pointer-events-none")
    item.dataset.purged = "true"
  }

  setAspectRatio (event) {
    event.preventDefault()
    const ratio = Number(event.params.ratio)
    if (!ratio || Number.isNaN(ratio)) return

    this.selectedAspectRatio = ratio
    this.syncRatioButtons()
    if (this.cropper) this.cropper.setAspectRatio(ratio)
  }

  cancelCrop (event) {
    event?.preventDefault()
    this.closeModal()
    this.queue = []
    this.currentOriginalFile = null
    this.editingAttachmentId = null
    this.pendingCropData = null
  }

  async confirmCrop (event) {
    event.preventDefault()
    if (!this.cropper) return

    const data = this.cropper.getData(true)
    const crop = {
      x: Math.round(data.x),
      y: Math.round(data.y),
      width: Math.round(data.width),
      height: Math.round(data.height),
      ratio: this.selectedAspectRatio
    }

    const { width, height } = this.outputDimensions()
    const canvas = this.cropper.getCroppedCanvas({
      width,
      height,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })
    const previewUrl = canvas ? canvas.toDataURL("image/jpeg", 0.92) : null
    const isUpdateOnly = Boolean(this.editingAttachmentId && !this.currentOriginalFile)

    const entry = {
      original: this.currentOriginalFile,
      crop,
      replaceId: isUpdateOnly ? "" : (this.editingAttachmentId || ""),
      attachmentId: isUpdateOnly ? this.editingAttachmentId : null,
      previewUrl,
      name: this.normalizedFilename(this.currentSourceName)
    }

    if (this.editingAttachmentId && !isUpdateOnly) {
      this.markAttachmentForPurge(this.editingAttachmentId)
    } else if (isUpdateOnly) {
      this.dimExistingAttachment(this.editingAttachmentId)
    }

    this.editingAttachmentId = null
    this.pendingCropData = null
    this.pendingEntries.push(entry)
    this.currentOriginalFile = null
    this.syncInputFiles()
    this.renderPendingList()
    this.closeModal()
    this.processQueue()
    if (previewUrl) this.dispatchPreviewUpdate(previewUrl)
  }

  processQueue () {
    const next = this.queue.shift()
    if (!next) return

    this.currentOriginalFile = next.originalFile || next.file
    const reader = new FileReader()
    reader.onload = () => this.openCropper(reader.result, next.file.name)
    reader.readAsDataURL(next.file)
  }

  openCropper (src, filename) {
    this.currentSourceName = filename
    this.cropImageTarget.src = src
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex", "items-center", "justify-center")
    this.modalTarget.setAttribute("aria-hidden", "false")
    this.syncRatioButtons()

    this.destroyCropper()
    // Wait for image layout inside the visible modal before initializing Cropper.
    requestAnimationFrame(() => {
      const cropData = this.pendingCropData
      this.cropper = new Cropper(this.cropImageTarget, {
        aspectRatio: this.selectedAspectRatio,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 1,
        responsive: true,
        background: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        ready: () => {
          if (!cropData || !this.cropper) return
          this.cropper.setData({
            x: cropData.x,
            y: cropData.y,
            width: cropData.width,
            height: cropData.height
          })
          this.pendingCropData = null
        }
      })
    })
  }

  closeModal () {
    this.destroyCropper()
    this.cropImageTarget.removeAttribute("src")
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex", "items-center", "justify-center")
    this.modalTarget.setAttribute("aria-hidden", "true")
  }

  destroyCropper () {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  syncInputFiles () {
    const fileTransfer = new DataTransfer()

    this.pendingEntries.forEach((entry) => {
      if (entry.original) fileTransfer.items.add(entry.original)
    })

    this.inputTarget.files = fileTransfer.files

    if (this.hasCropsTarget) {
      this.cropsTarget.innerHTML = ""
      this.pendingEntries.forEach((entry) => {
        if (!entry.original) return

        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "product[image_crops][]"
        input.value = JSON.stringify(entry.crop)
        this.cropsTarget.appendChild(input)
      })
    }

    if (this.hasCropUpdatesTarget) {
      this.cropUpdatesTarget.innerHTML = ""
      this.pendingEntries.forEach((entry) => {
        if (!entry.attachmentId || entry.original) return

        const input = document.createElement("input")
        input.type = "hidden"
        input.name = `product[image_crop_updates][${entry.attachmentId}]`
        input.value = JSON.stringify(entry.crop)
        this.cropUpdatesTarget.appendChild(input)
      })
    }

    if (this.hasReplacementsTarget) {
      this.replacementsTarget.innerHTML = ""
      this.pendingEntries.forEach((entry) => {
        if (!entry.original) return

        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "product[image_crop_replacements][]"
        input.value = entry.replaceId || ""
        this.replacementsTarget.appendChild(input)
      })
    }

    if (this.hasFilenameTarget) {
      if (this.pendingEntries.length === 0) {
        this.filenameTarget.textContent = ""
        this.filenameTarget.classList.add("hidden")
      } else if (this.pendingEntries.length === 1) {
        this.filenameTarget.textContent = this.pendingEntries[0].name
        this.filenameTarget.classList.remove("hidden")
      } else {
        this.filenameTarget.textContent = this.pendingCountLabelValue.replace("%{count}", String(this.pendingEntries.length))
        this.filenameTarget.classList.remove("hidden")
      }
    }
  }

  renderPendingList () {
    if (!this.hasPendingListTarget) return

    this.revokePendingObjectUrls()
    this.pendingListTarget.innerHTML = ""
    this.pendingEntries.forEach((entry, index) => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative aspect-[3/4] rounded-lg overflow-hidden bg-background-light border border-input-border-color"

      if (entry.previewUrl) {
        const img = document.createElement("img")
        img.src = entry.previewUrl
        img.alt = entry.name
        img.className = "w-full h-full object-contain object-center bg-background-light"
        wrapper.appendChild(img)
      }

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "absolute top-1 right-1 rounded-full bg-black/60 text-white text-xs px-2 py-1"
      remove.textContent = "×"
      remove.addEventListener("click", () => {
        const removed = this.pendingEntries.splice(index, 1)[0]
        if (removed?.attachmentId) this.undimExistingAttachment(removed.attachmentId)
        this.syncInputFiles()
        this.renderPendingList()
      })
      wrapper.appendChild(remove)

      this.pendingListTarget.appendChild(wrapper)
    })
  }

  revokePendingObjectUrls () {
    this.pendingObjectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.pendingObjectUrls = []
  }

  markAttachmentForPurge (attachmentId) {
    const item = this.existingItemTargets.find((el) => el.dataset.attachmentId === String(attachmentId))
    if (!item) return

    const checkbox = item.querySelector('input[type="checkbox"][name="product[purge_image_ids][]"]')
    if (checkbox) checkbox.checked = true
    item.classList.add("opacity-40", "pointer-events-none")
    item.dataset.purged = "true"
  }

  dimExistingAttachment (attachmentId) {
    const item = this.existingItemTargets.find((el) => el.dataset.attachmentId === String(attachmentId))
    if (!item) return

    item.classList.add("opacity-40")
    item.dataset.cropUpdatePending = "true"
  }

  undimExistingAttachment (attachmentId) {
    const item = this.existingItemTargets.find((el) => el.dataset.attachmentId === String(attachmentId))
    if (!item || item.dataset.purged === "true") return

    item.classList.remove("opacity-40")
    delete item.dataset.cropUpdatePending
  }

  parseCropJson (raw) {
    if (!raw) return null

    try {
      const data = JSON.parse(raw)
      if (!data || typeof data !== "object") return null
      return data
    } catch (_error) {
      return null
    }
  }

  outputDimensions () {
    const ratio = this.selectedAspectRatio || 1
    const longEdge = this.outputSizeValue

    if (ratio >= 1) {
      return {
        width: longEdge,
        height: Math.round(longEdge / ratio)
      }
    }

    return {
      width: Math.round(longEdge * ratio),
      height: longEdge
    }
  }

  normalizedFilename (name) {
    const base = (name || "image").replace(/\.[^/.]+$/, "")
    const key = String(this.selectedAspectRatio)
    const label = RATIO_LABELS[key] || key.replace(".", "-")
    return `${base}-${label}.jpg`
  }

  syncRatioButtons () {
    if (!this.hasRatioButtonTarget) return

    this.ratioButtonTargets.forEach((button) => {
      const ratio = Number(button.dataset.productImagesCropRatioParam)
      const active = Math.abs(ratio - this.selectedAspectRatio) < 0.001
      button.classList.toggle("bg-button-primary-bg", active)
      button.classList.toggle("text-button-primary-text", active)
      button.classList.toggle("border-button-primary-bg", active)
      button.classList.toggle("bg-background", !active)
      button.classList.toggle("text-copy-color-one", !active)
      button.classList.toggle("border-background-light", !active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  dispatchPreviewUpdate (dataUrl) {
    this.element.dispatchEvent(new CustomEvent("product-images-crop:changed", {
      bubbles: true,
      detail: { dataUrl }
    }))
  }
}
